import { HOTEL_PRICE_LEVEL_BOUNDS } from './hotel-avg-room-night';

/**
 * The Mini Planner card's words (XT-840) — what a product's card says, from
 * the product's own fields. Shared by the two places that build the card: the
 * planner's web model, which renders it for a travel agent, and the agent
 * platform, which hands the same card to Claude (XT-953). One copy, so the two
 * never describe a product differently. Pure: values in, strings out.
 */

const PARAGRAPH_WORDS = 100;

const ENTITIES: Record<string, string> = {
  amp: '&',
  nbsp: ' ',
  quot: '"',
  lt: '<',
  gt: '>',
  '#39': "'",
  apos: "'",
};

/** Rich text as plain paragraphs. */
export const htmlParagraphs = (html: string | null | undefined): string[] =>
  (html ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(p|div|li|ul|ol|h[1-6])(\s[^>]*)?>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&([a-z]+|#\d+);/gi, (entity, name: string) => {
      if (ENTITIES[name.toLowerCase()] !== undefined) {
        return ENTITIES[name.toLowerCase()] as string;
      }
      const code = /^#(\d+)$/.exec(name)?.[1];
      return code ? String.fromCharCode(Number(code)) : entity;
    })
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

/** The first `words` words, with an ellipsis when there were more. */
export const capWords = (text: string, words = PARAGRAPH_WORDS): string => {
  const list = text.split(' ');
  return list.length <= words ? text : `${list.slice(0, words).join(' ')}…`;
};

/** The English marketing sections' texts, in order. */
export const marketingTexts = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const english =
    value.find(
      (entry) =>
        entry &&
        typeof entry === 'object' &&
        (entry as { languageCode?: unknown }).languageCode === 'en'
    ) ?? null;
  const sections = (english as { sections?: unknown } | null)?.sections;
  if (!Array.isArray(sections)) return [];
  return sections.flatMap((section) => {
    const text = (section as { text?: unknown } | null)?.text;
    return typeof text === 'string' && text.trim()
      ? [text.replace(/\s+/g, ' ').trim()]
      : [];
  });
};

/** Where the product starts: its first leg's start location. */
export const meetingPointOf = (legs: unknown): string | null => {
  if (!Array.isArray(legs)) return null;
  const location = (
    legs[0] as { details?: { startLocation?: { title?: unknown } } } | null
  )?.details?.startLocation;
  const title = location?.title;
  return typeof title === 'string' && title.trim() ? title.trim() : null;
};

const legClockMinutes = (value: unknown): number | null => {
  if (!value || typeof value !== 'object') return null;
  const { hours, minutes } = value as { hours?: unknown; minutes?: unknown };
  if (typeof hours !== 'number' || typeof minutes !== 'number') return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

/** First leg start → end, the same span the planner shows; past midnight wraps. */
export const productDurationMinutes = (legs: unknown): number | null => {
  if (!Array.isArray(legs) || legs.length === 0) return null;
  const details = (legs[0] as { details?: unknown })?.details as
    | { startTime?: unknown; endTime?: unknown }
    | undefined;
  const start = legClockMinutes(details?.startTime);
  const end = legClockMinutes(details?.endTime);
  if (start === null || end === null || start === end) return null;
  return end > start ? end - start : end + 24 * 60 - start;
};

/** The short marketing text's first sentence, for one line on the card. */
export const strapline = (text: string | null | undefined): string | null => {
  const trimmed = text?.replace(/\s+/g, ' ').trim();
  if (!trimmed) return null;
  const [first] = trimmed.split(/(?<=[.!?])\s+/);
  return first?.trim() || null;
};

/**
 * "3h", "2h30", "45 min", or "Full day or longer" from 10 hours: legs keep
 * only clock times, so a two-night boat cannot be told from a long day.
 * Hotels and restaurants have no duration.
 */
export const cardDuration = (
  kind: string,
  minutes: number | null | undefined
): string | null => {
  if (kind !== 'experience' || !minutes || minutes <= 0) return null;
  if (minutes >= 600) return 'Full day or longer';
  if (minutes < 45) return `${minutes} min`;
  const rounded = Math.max(60, Math.round(minutes / 30) * 30);
  const hours = Math.floor(rounded / 60);
  return rounded % 60 === 0 ? `${hours}h` : `${hours}h30`;
};

/** A restaurant's service from its default slot: lunch before 16:00, dinner from 18:00. */
export const servicePeriod = (
  startMinutes: number | null | undefined
): string | null => {
  if (startMinutes == null) return null;
  if (startMinutes < 11 * 60) return 'Breakfast';
  if (startMinutes < 16 * 60) return 'Lunch';
  if (startMinutes >= 18 * 60) return 'Dinner';
  return null;
};

/** A hashtag compared without case, spaces or punctuation ("Fine Dining" is "FineDining"). */
export const hashtagKey = (tag: string): string =>
  tag.toLowerCase().replace(/[^a-z0-9]/g, '');

/** "FineDining" → "Fine Dining". */
export const humanTag = (tag: string): string =>
  tag.replace(/([a-z])([A-Z])/g, '$1 $2').trim();

const HOTEL_SETTINGS = new Set([
  'urban',
  'beach',
  'countryside',
  'boutique',
  'luxury',
  'ecotourism',
  'golf',
  'landmark',
  'historic',
  'nature',
  'wine',
]);

const RESTAURANT_FORMATS = new Set([
  'finedining',
  'casualdining',
  'comfortfood',
  'streetfood',
  'tapas',
  'seafood',
]);

const AWARDS = new Set(['bibgourmand', 'michelin', 'michelinstar']);

const pick = (tags: readonly string[], set: ReadonlySet<string>) =>
  tags.filter((tag) => set.has(hashtagKey(tag))).map(humanTag);

/** A hotel's setting: up to two of its setting hashtags. */
export const hotelSetting = (tags: readonly string[]) =>
  pick(tags, HOTEL_SETTINGS).slice(0, 2);

/** A restaurant's format hashtag (Fine Dining, Casual Dining…). */
export const restaurantFormat = (tags: readonly string[]): string | null =>
  pick(tags, RESTAURANT_FORMATS)[0] ?? null;

/** Awards the catalogue states as hashtags; never read from prose. */
export const awardTags = (tags: readonly string[]) => pick(tags, AWARDS);

/** "Monday", "Monday and Tuesday", "Monday, Tuesday and Sunday". */
export const closedDaysLabel = (days: readonly string[]): string | null => {
  if (days.length === 0) return null;
  if (days.length === 1) return days[0] ?? null;
  return `${days.slice(0, -1).join(', ')} and ${days.at(-1)}`;
};

/**
 * A product's star class (half stars kept); null when unrated. The catalogue
 * stores unrated properties as 1.0, so anything below 2 reads as unrated.
 */
export const hotelStars = (value: unknown): number | null => {
  const stars = typeof value === 'string' ? Number(value) : value;
  if (typeof stars !== 'number' || !Number.isFinite(stars) || stars < 2) {
    return null;
  }
  return Math.min(5, Math.floor(stars * 2) / 2);
};

/** `€€` for level 2. */
export const priceLevelSymbol = (level: number): string => '€'.repeat(level);

/** `under €150` · `€150–300` · `€500+` — what a level means per night. */
export const priceLevelRange = (level: number): string => {
  const low = HOTEL_PRICE_LEVEL_BOUNDS[level - 2];
  const high = HOTEL_PRICE_LEVEL_BOUNDS[level - 1];
  if (low === undefined) return `under €${high}`;
  if (high === undefined) return `€${low}+`;
  return `€${low}–${high}`;
};
